<template>
    <v-dialog
        v-model="isOpen"
        max-width="90vw"
        persistent
        content-class="media-modal-dialog"
    >
        <v-card class="media-modal-card" :class="{ 'dark-mode': (isDarkMode && mode === 'media') || (isDarkMode && mode === 'print') || mode === 'flyer', 'light-mode': !isDarkMode && mode === 'media', 'print-mode': mode === 'print' }">
            <!-- SKU Badge (top left) -->
            <div class="sku-badge" :class="{ 'dark': (isDarkMode && mode === 'media') || (isDarkMode && mode === 'print') || mode === 'flyer' }">
                {{ currentItem.label || 'Media' }}
            </div>

            <!-- Close Button -->
            <v-btn
                icon
                class="close-button"
                @click="closeModal"
                :color="(isDarkMode && mode === 'media') || (isDarkMode && mode === 'print') || mode === 'flyer' ? 'white' : 'black'"
            >
                <v-icon>mdi-close</v-icon>
            </v-btn>

            <!-- Dark/Light Mode Toggle (only for print mode - for print previews) -->
            <v-btn
                v-if="mode === 'print'"
                icon
                class="mode-toggle-button"
                @click="toggleDarkMode"
                :color="isDarkMode ? 'white' : 'black'"
            >
                <v-icon>{{ isDarkMode ? 'mdi-white-balance-sunny' : 'mdi-moon-waning-crescent' }}</v-icon>
            </v-btn>

            <!-- Navigation Arrows (only show if multiple items) -->
            <v-btn
                v-if="items.length > 1"
                icon
                class="nav-button nav-button-left"
                @click="previousItem"
                :color="(isDarkMode && mode === 'media') || (isDarkMode && mode === 'print') || mode === 'flyer' ? 'white' : 'black'"
            >
                <v-icon large>mdi-chevron-left</v-icon>
            </v-btn>
            <v-btn
                v-if="items.length > 1"
                icon
                class="nav-button nav-button-right"
                @click="nextItem"
                :color="(isDarkMode && mode === 'media') || (isDarkMode && mode === 'print') || mode === 'flyer' ? 'white' : 'black'"
            >
                <v-icon large>mdi-chevron-right</v-icon>
            </v-btn>

            <!-- Image Container -->
            <div class="image-container" @click="handleBackgroundClick">
                <div class="image-wrapper">
                    <img
                        :src="currentItem.url"
                        :alt="currentItem.label || 'Media'"
                        :style="mode !== 'print' ? { backgroundColor: currentItem.bg || 'transparent' } : {}"
                        class="media-image"
                    />
                </div>
            </div>

            <!-- Pagination Info (only if multiple items) -->
            <div v-if="items.length > 1" class="media-info">
                <div class="pagination-dots">
                    <span
                        v-for="(item, index) in items"
                        :key="index"
                        class="dot"
                        :class="{ active: index === currentIndex }"
                        @click="goToItem(index)"
                    ></span>
                </div>
                <div class="pagination-text">
                    {{ currentIndex + 1 }} / {{ items.length }}
                </div>
            </div>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: 'MediaModal',
    props: {
        value: {
            type: Boolean,
            default: false
        },
        items: {
            type: Array,
            required: true,
            validator: (items) => {
                return items.every(item => item.url);
            }
        },
        initialIndex: {
            type: Number,
            default: 0
        },
        mode: {
            type: String,
            default: 'media',
            validator: (value) => ['media', 'print', 'flyer'].includes(value)
        }
    },
    data() {
        return {
            currentIndex: 0,
            isDarkMode: false  // Default to light mode for print previews
        };
    },
    computed: {
        isOpen: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit('input', val);
            }
        },
        currentItem() {
            if (this.items.length === 0) {
                return { url: '', label: '', bg: '' };
            }
            return this.items[this.currentIndex] || this.items[0];
        }
    },
    watch: {
        value(newVal) {
            if (newVal) {
                // Reset to initial index when modal opens
                this.currentIndex = this.initialIndex;
                // Add keyboard event listener
                window.addEventListener('keydown', this.handleKeydown);
            } else {
                // Remove keyboard event listener
                window.removeEventListener('keydown', this.handleKeydown);
            }
        }
    },
    methods: {
        closeModal() {
            this.isOpen = false;
            this.$emit('close');
        },
        nextItem() {
            if (this.currentIndex < this.items.length - 1) {
                this.currentIndex++;
            } else {
                this.currentIndex = 0; // Loop back to first
            }
        },
        previousItem() {
            if (this.currentIndex > 0) {
                this.currentIndex--;
            } else {
                this.currentIndex = this.items.length - 1; // Loop to last
            }
        },
        goToItem(index) {
            this.currentIndex = index;
        },
        handleKeydown(event) {
            if (!this.isOpen) return;

            switch (event.key) {
                case 'Escape':
                    this.closeModal();
                    break;
                case 'ArrowLeft':
                    if (this.items.length > 1) {
                        this.previousItem();
                    }
                    break;
                case 'ArrowRight':
                    if (this.items.length > 1) {
                        this.nextItem();
                    }
                    break;
            }
        },
        handleBackgroundClick(event) {
            // Close modal if clicking on background (not the image)
            if (event.target.classList.contains('image-container')) {
                this.closeModal();
            }
        },
        toggleDarkMode() {
            this.isDarkMode = !this.isDarkMode;
            console.log('🌓 Mode toggled:', this.isDarkMode ? 'Dark' : 'Light');
        }
    },
    beforeDestroy() {
        // Clean up event listener
        window.removeEventListener('keydown', this.handleKeydown);
    }
};
</script>

<style scoped>
.media-modal-card {
    position: relative;
    border-radius: 8px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    max-height: 95dvh;
    height: 95dvh;
    transition: background-color 0.3s ease;
}

/* Dark Mode (default for media) */
.media-modal-card.dark-mode {
    background: rgba(0, 0, 0, 0.95);
}

/* Light Mode (for media) */
.media-modal-card.light-mode {
    background: rgba(255, 255, 255, 0.98);
}

/* Print Mode - with toggle support */
.media-modal-card.print-mode {
    background: #ffffff;
    transition: background-color 0.3s ease;
}

/* Print Mode Dark - when toggled */
.media-modal-card.print-mode.dark-mode {
    background: rgba(0, 0, 0, 0.95);
}

/* SKU Badge (top left) */
.sku-badge {
    position: absolute;
    top: 16px;
    left: 16px;
    z-index: 10;
    background: rgba(255, 255, 255, 0.9);
    color: #333;
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 600;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    transition: all 0.3s ease;
    border: 1px solid rgba(0, 0, 0, 0.1);
}

.sku-badge.dark {
    background: rgba(0, 0, 0, 0.8);
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.2);
}

/* Close Button */
.close-button {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 10;
    background: rgba(0, 0, 0, 0.5) !important;
    transition: all 0.3s ease;
}

.light-mode .close-button,
.print-mode:not(.dark-mode) .close-button {
    background: rgba(255, 255, 255, 0.8) !important;
    border: 1px solid rgba(0, 0, 0, 0.2);
}

.close-button:hover {
    background: rgba(0, 0, 0, 0.8) !important;
    transform: rotate(90deg);
}

.light-mode .close-button:hover,
.print-mode:not(.dark-mode) .close-button:hover {
    background: rgba(255, 255, 255, 1) !important;
}

/* Mode Toggle Button */
.mode-toggle-button {
    position: absolute;
    top: 16px;
    right: 72px;
    z-index: 10;
    background: rgba(0, 0, 0, 0.5) !important;
    transition: all 0.3s ease;
}

.light-mode .mode-toggle-button,
.print-mode:not(.dark-mode) .mode-toggle-button {
    background: rgba(255, 255, 255, 0.8) !important;
    border: 1px solid rgba(0, 0, 0, 0.2);
}

.mode-toggle-button:hover {
    background: rgba(0, 0, 0, 0.8) !important;
    transform: scale(1.1);
}

.light-mode .mode-toggle-button:hover,
.print-mode:not(.dark-mode) .mode-toggle-button:hover {
    background: rgba(255, 255, 255, 1) !important;
}

/* Navigation Buttons */
.nav-button {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
    background: rgba(0, 0, 0, 0.5) !important;
    transition: all 0.3s ease;
}

.light-mode .nav-button,
.print-mode:not(.dark-mode) .nav-button {
    background: rgba(255, 255, 255, 0.8) !important;
    border: 1px solid rgba(0, 0, 0, 0.2);
}

.nav-button:hover {
    background: rgba(0, 0, 0, 0.8) !important;
    transform: translateY(-50%) scale(1.1);
}

.light-mode .nav-button:hover,
.print-mode:not(.dark-mode) .nav-button:hover {
    background: rgba(255, 255, 255, 1) !important;
}

.nav-button-left {
    left: 16px;
}

.nav-button-right {
    right: 16px;
}

/* Image Container */
.image-container {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 60px 80px 20px 80px;
    overflow: hidden;
    cursor: pointer;
    min-height: 0;
}

.image-wrapper {
    max-width: 100%;
    max-height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.media-image {
    max-width: 100%;
    max-height: calc(100dvh - 200px);
    object-fit: contain;
    border-radius: 4px;
    transition: transform 0.3s ease;
}

/* Media Info */
.media-info {
    padding: 16px 24px;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    transition: all 0.3s ease;
    flex-shrink: 0;
}

.light-mode .media-info {
    background: rgba(255, 255, 255, 0.95);
    border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.print-mode:not(.dark-mode) .media-info {
    background: #f5f5f5;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.pagination-dots {
    display: flex;
    gap: 8px;
    align-items: center;
}

.dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.3);
    cursor: pointer;
    transition: all 0.3s ease;
}

.light-mode .dot,
.print-mode:not(.dark-mode) .dot {
    background: rgba(0, 0, 0, 0.3);
}

.dot:hover {
    background: rgba(255, 255, 255, 0.6);
    transform: scale(1.2);
}

.light-mode .dot:hover,
.print-mode:not(.dark-mode) .dot:hover {
    background: rgba(0, 0, 0, 0.6);
}

.dot.active {
    background: white;
    transform: scale(1.3);
}

.light-mode .dot.active,
.print-mode:not(.dark-mode) .dot.active {
    background: #333;
}

.pagination-text {
    color: rgba(255, 255, 255, 0.7);
    font-size: 14px;
    font-weight: 500;
    min-width: 50px;
    text-align: right;
    transition: color 0.3s ease;
}

.light-mode .pagination-text,
.print-mode:not(.dark-mode) .pagination-text {
    color: rgba(0, 0, 0, 0.7);
}

/* Responsive adjustments */
/* Tablet (768px and below) */
@media (max-width: 768px) {
    .media-modal-card {
        max-height: 95dvh;
        height: 95dvh;
    }

    .image-container {
        padding: 50px 30px 15px 30px;
    }

    .media-image {
        max-height: calc(95dvh - 180px);
    }
}

/* Mobile (600px and below) */
@media (max-width: 600px) {
    .media-modal-card {
        max-height: 95dvh;
        height: 95dvh;
    }

    .image-container {
        padding: 45px 15px 12px 15px;
    }

    .media-image {
        max-height: calc(95dvh - 160px);
    }

    .nav-button-left {
        left: 8px;
    }

    .nav-button-right {
        right: 8px;
    }

    .close-button {
        top: 8px;
        right: 8px;
    }

    .media-info {
        flex-direction: column;
        gap: 8px;
        align-items: flex-start;
    }

    .pagination-text {
        align-self: flex-end;
    }
}

/* Animations */
@keyframes fadeIn {
    from {
        opacity: 0;
        transform: scale(0.95);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
}

.media-image {
    animation: fadeIn 0.3s ease;
}
</style>

<style>
/* Global style for dialog positioning */
.media-modal-dialog {
    margin: 24px;
}

.media-modal-dialog .v-card {
    overflow: hidden;
}
</style>
